// PQRSD email templates.
//
// Each export builds { subject, html } for one notification type. Keep
// templates terse and link back to the portal — avoid embedding sensitive
// response bodies in email.
//
// Canonical source. Each cloud function that sends email keeps a local copy
// alongside its index.js (same deploy pattern as businessDays.js).

const { PQRSD_TYPES_LABEL } = (() => {
  // Inlined since cloud functions can't import from src/constants.js.
  const m = {
    peticion_general: "Petición general",
    consulta: "Petición de consulta",
    info: "Solicitud de información o documentación",
    queja: "Queja",
    reclamo: "Reclamo",
    denuncia: "Denuncia",
    sugerencia: "Sugerencia",
    felicitacion: "Felicitación",
  };
  return { PQRSD_TYPES_LABEL: m };
})();

function esc(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function fmtDate(d) {
  if (!d) return "";
  const date = d.toDate ? d.toDate() : new Date(d);
  return date.toLocaleDateString("es-CO", {
    weekday: "long", year: "numeric", month: "long", day: "numeric"
  });
}

// ---------- brand (zero-config, env-driven) ----------
// Colors and name come from the instance's environment, never from the
// template itself — so a new instance is branded by setting env vars, with no
// template edits and no per-instance copies.
//
//   Client build (webpack inlines REACT_APP_*):  REACT_APP_THEME (the origin
//   theme color, e.g. #00652e) and REACT_APP_EMAIL_FROM_NAME.
//   Cloud functions runtime:                     PQRSD_BRAND_COLOR and
//   EMAIL_FROM_NAME.
// Any unset value falls back to the shared default, so existing instances keep
// working untouched.

const DEFAULT_BRAND_COLOR = "#00652e";

// Optional override, set from the shared config/global doc so a new
// instance is branded centrally (no env redeploy, no template edits). Only
// truthy values override — an empty config field falls through to the env /
// default chain below. Callers set this immediately before rendering
// (templates are synchronous, so there is no interleaving risk).
let _brandOverride = { color: "", name: "", logo: "", logoWidth: 0 };

function setBrand({ color, name, logo, logoWidth } = {}) {
  _brandOverride = {
    color: color || "",
    name: name || "",
    logo: logo || "",
    logoWidth: Number(logoWidth) > 0 ? Number(logoWidth) : 0,
  };
}

function brandColor() {
  const c =
    _brandOverride.color ||
    process.env.REACT_APP_THEME ||
    process.env.PQRSD_BRAND_COLOR ||
    DEFAULT_BRAND_COLOR;
  return /^#[0-9a-fA-F]{6}$/.test(c) ? c : DEFAULT_BRAND_COLOR;
}

function brandName() {
  return (
    _brandOverride.name ||
    process.env.REACT_APP_EMAIL_FROM_NAME ||
    process.env.EMAIL_FROM_NAME ||
    "Sistema PQRSD"
  );
}

// Optional external logo (absolute https URL). Rendered in the header band when
// present; empty = header shows the brand name only (previous behavior).
function brandLogo() {
  const u =
    _brandOverride.logo ||
    process.env.REACT_APP_EMAIL_LOGO_URL ||
    process.env.EMAIL_LOGO_URL ||
    "";
  return /^https?:\/\//i.test(u) ? u : "";
}

// Rendered logo width in px. Carries across every template so the admin sets it
// once (in the preview) and all mails match.
const DEFAULT_LOGO_WIDTH = 140;
function brandLogoWidth() {
  const w =
    _brandOverride.logoWidth ||
    Number(process.env.REACT_APP_EMAIL_LOGO_WIDTH) ||
    Number(process.env.EMAIL_LOGO_WIDTH) ||
    DEFAULT_LOGO_WIDTH;
  return Math.min(Math.max(Number(w) || DEFAULT_LOGO_WIDTH, 40), 320);
}

// Blend a #rrggbb color toward white; used for the soft title band so any
// origin color yields a matching light tint automatically.
function tint(hex, amount = 0.88) {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex);
  if (!m) return "#eef5f0";
  const n = parseInt(m[1], 16);
  const chans = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  return (
    "#" +
    chans
      .map((c) => Math.round(c + (255 - c) * amount).toString(16).padStart(2, "0"))
      .join("")
  );
}

// Card-style shell: colored header band (name only, no logo), a soft tinted
// title band, a white body, and a muted footer — all keyed off the origin
// brand color.
function shell(title, bodyHtml) {
  const color = brandColor();
  const band = tint(color);
  const name = esc(brandName());
  const logo = brandLogo();
  const logoCell = logo
    ? `<td align="right" style="vertical-align:middle;"><img src="${esc(logo)}" alt="${name}" width="${brandLogoWidth()}" style="display:inline-block;width:${brandLogoWidth()}px;max-width:100%;height:auto;border:0;">` + "</td>"
    : "";
  return `<!doctype html><html><body style="margin:0;padding:0;background:#f4f5f7;">
<div style="font-family:Arial,Helvetica,sans-serif;line-height:1.5;color:#333;max-width:600px;margin:0 auto;padding:24px 12px;">
  <div style="background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
    <div style="background:${color};padding:22px 32px;">
      <table role="presentation" width="100%" style="border-collapse:collapse;"><tr>
        <td style="vertical-align:middle;"><span style="color:#ffffff;font-size:18px;font-weight:700;">${name}</span></td>
        ${logoCell}
      </tr></table>
    </div>
    <div style="background:${band};padding:20px 32px;">
      <h1 style="margin:0;color:${color};font-size:22px;font-weight:700;">${esc(title)}</h1>
    </div>
    <div style="padding:28px 32px;font-size:15px;color:#333;">
      ${bodyHtml}
      <p style="font-size:12px;color:#9aa0a6;margin:28px 0 0;">Este es un mensaje automático del sistema PQRSD. Por favor no responda a este correo.</p>
    </div>
  </div>
  <p style="text-align:center;color:#9aa0a6;font-size:12px;margin:18px 0 0;">${name} · Sistema de PQRSD</p>
</div>
</body></html>`;
}

function btn(href, label, color) {
  const c = color || brandColor();
  return `<a href="${esc(href)}" style="display:inline-block;padding:12px 22px;background:${c};color:#fff;text-decoration:none;border-radius:8px;font-weight:600;margin:4px 8px 4px 0;">${esc(label)}</a>`;
}

// ---------- citizen templates ----------

function citizenSubmission({ pqrsd, radicado, referenceId, magicLink }) {
  const typeLabel = PQRSD_TYPES_LABEL[pqrsd.type] || pqrsd.type;
  const subject = `PQRSD radicada — ${radicado}`;
  const html = shell("Hemos recibido su PQRSD", `
    <p>Su solicitud ha sido radicada exitosamente. Conserve los siguientes datos para hacer seguimiento:</p>
    <table style="border-collapse:collapse;margin:16px 0;">
      <tr><td style="padding:6px 12px 6px 0;color:#666;">Radicado:</td><td style="padding:6px 0;"><strong>${esc(radicado)}</strong></td></tr>
      <tr><td style="padding:6px 12px 6px 0;color:#666;">Tipo:</td><td style="padding:6px 0;">${esc(typeLabel)}</td></tr>
      <tr><td style="padding:6px 12px 6px 0;color:#666;">Asunto:</td><td style="padding:6px 0;">${esc(pqrsd.subject || "")}</td></tr>
      <tr><td style="padding:6px 12px 6px 0;color:#666;">Plazo de respuesta:</td><td style="padding:6px 0;">${esc(fmtDate(pqrsd.slaDeadline))}</td></tr>
    </table>
    <p>Puede consultar el estado en cualquier momento con un solo clic:</p>
    <p>${btn(magicLink, "Ver el estado de mi PQRSD")}</p>
    <p style="font-size:12px;color:#666;">Si el botón no funciona, ingrese a la página de seguimiento con el número de radicado y el correo registrado.</p>
  `);
  return { subject, html };
}

function citizenInProgress({ radicado, magicLink }) {
  return {
    subject: `Su PQRSD ${radicado} está en trámite`,
    html: shell("Su PQRSD está siendo gestionada", `
      <p>Le informamos que un funcionario está trabajando en su solicitud con radicado <strong>${esc(radicado)}</strong>.</p>
      <p>${btn(magicLink, "Ver estado")}</p>
    `),
  };
}

function citizenResponseInterim({ radicado, newDeadline, magicLink }) {
  return {
    subject: `Avance sobre su PQRSD ${radicado}`,
    html: shell("Le compartimos un avance", `
      <p>Hemos publicado una respuesta interina (informe de avance) sobre su PQRSD <strong>${esc(radicado)}</strong>.</p>
      <p>Continuamos trabajando para darle una respuesta definitiva. Nuevo plazo: <strong>${esc(fmtDate(newDeadline))}</strong>.</p>
      <p>${btn(magicLink, "Ver el avance")}</p>
    `),
  };
}

// The citizen reviews the answer and rates it on /pqrsd/estado (the survey the
// admins configure); reopening is one of the survey's options, so the email
// carries a single call to action. `confirmLink` is the pre-survey caller's
// name for the same link.
function citizenResponseFinal({ radicado, magicLink, rateLink, confirmLink }) {
  const rate = rateLink || confirmLink || magicLink;
  return {
    subject: `Respuesta a su PQRSD ${radicado}`,
    html: shell("Respuesta final a su PQRSD", `
      <p>Hemos publicado la respuesta final a su PQRSD <strong>${esc(radicado)}</strong>.</p>
      <p>${btn(magicLink, "Ver la respuesta")}</p>
      <p style="margin-top:24px;">Después de leerla, cuéntenos si la respuesta atiende su solicitud.
      Si no es así, desde la misma página puede reabrirla e indicarnos qué necesita.</p>
      <p>${btn(rate, "Calificar la respuesta", "#1f7a3a")}</p>
    `),
  };
}

// ---------- requerimiento de información (CPACA art. 17) ----------

// Renders the officer-authored items as the list the citizen must satisfy.
function itemsList(items) {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return "";
  const rows = list.map((it) => {
    const label = it?.kind === "file" ? it.label : it.header;
    const tag = it?.kind === "file" ? "Documento" : "Pregunta";
    const help = it?.help ? `<div style="color:#666;font-size:13px;margin-top:2px;">${esc(it.help)}</div>` : "";
    const optional = it?.required === false
      ? ` <span style="color:#9aa0a6;font-size:12px;">(opcional)</span>`
      : "";
    return `<li style="margin:0 0 10px;"><span style="color:#666;font-size:12px;text-transform:uppercase;letter-spacing:.4px;">${tag}</span><br><strong>${esc(label)}</strong>${optional}${help}</li>`;
  });
  return `<ul style="padding-left:18px;margin:16px 0;">${rows.join("")}</ul>`;
}

function citizenInfoRequested({ radicado, items, dueAt, replyLink, note }) {
  return {
    subject: `Necesitamos información adicional — PQRSD ${radicado}`,
    html: shell("Necesitamos información adicional", `
      <p>Para poder resolver de fondo su PQRSD <strong>${esc(radicado)}</strong> necesitamos que la complete
      con la siguiente información:</p>
      ${itemsList(items)}
      ${note ? `<p style="background:#fff8e1;border-left:3px solid #b45a00;padding:10px 14px;margin:16px 0;">${esc(note)}</p>` : ""}
      <p>Puede diligenciarla en línea, con un solo clic y sin registrarse:</p>
      <p>${btn(replyLink, "Completar mi solicitud", "#b45a00")}</p>
      <p>Tiene plazo hasta el <strong>${esc(fmtDate(dueAt))}</strong>. Si necesita más tiempo, puede solicitar
      una prórroga desde el mismo enlace antes de esa fecha.</p>
      <p style="font-size:13px;color:#666;">Mientras esperamos su respuesta, el término legal para resolver su
      petición queda suspendido y se reactivará al día siguiente de recibir lo solicitado. Si no recibimos la
      información dentro del plazo, la entidad podrá decretar el desistimiento tácito y archivar la solicitud
      (artículo 17 de la Ley 1755 de 2015).</p>
    `),
  };
}

function citizenInfoReminder({ radicado, dueAt, replyLink, daysLeft }) {
  const when = daysLeft === 0
    ? "Hoy vence el plazo"
    : `Quedan ${daysLeft} día${daysLeft === 1 ? "" : "s"}`;
  return {
    subject: `Recordatorio — información pendiente en su PQRSD ${radicado}`,
    html: shell("Su solicitud está pendiente de información", `
      <p>Aún no hemos recibido la información que le solicitamos para poder resolver su PQRSD
      <strong>${esc(radicado)}</strong>.</p>
      <p><strong>${esc(when)}</strong> — la fecha límite es el <strong>${esc(fmtDate(dueAt))}</strong>.</p>
      <p>${btn(replyLink, "Completar mi solicitud", "#b45a00")}</p>
      <p style="font-size:13px;color:#666;">Si no recibimos lo solicitado, la entidad podrá decretar el
      desistimiento tácito y archivar la solicitud (artículo 17 de la Ley 1755 de 2015). Si necesita más tiempo,
      solicite una prórroga desde el mismo enlace antes del vencimiento.</p>
    `),
  };
}

function citizenInfoReceived({ radicado, newDeadline, magicLink }) {
  return {
    subject: `Recibimos su información — PQRSD ${radicado}`,
    html: shell("Recibimos la información solicitada", `
      <p>Gracias. Registramos la información que nos envió para su PQRSD <strong>${esc(radicado)}</strong>
      y retomamos el trámite.</p>
      ${newDeadline ? `<p>Nueva fecha límite de respuesta: <strong>${esc(fmtDate(newDeadline))}</strong>.</p>` : ""}
      <p>${btn(magicLink, "Ver el estado de mi PQRSD")}</p>
    `),
  };
}

function citizenInfoExtensionGranted({ radicado, dueAt, replyLink }) {
  return {
    subject: `Prórroga concedida — PQRSD ${radicado}`,
    html: shell("Le concedimos una prórroga", `
      <p>Ampliamos el plazo para completar la información de su PQRSD <strong>${esc(radicado)}</strong>.</p>
      <p>Nueva fecha límite: <strong>${esc(fmtDate(dueAt))}</strong>.</p>
      <p>${btn(replyLink, "Completar mi solicitud", "#b45a00")}</p>
    `),
  };
}

function citizenDesistimiento({ radicado, magicLink, reason, appealLink, appealLastDay }) {
  return {
    subject: `Desistimiento y archivo — PQRSD ${radicado}`,
    html: shell("Su solicitud fue archivada", `
      <p>Como no recibimos la información que le solicitamos dentro del plazo legal, la entidad decretó el
      <strong>desistimiento tácito</strong> de su PQRSD <strong>${esc(radicado)}</strong> y ordenó su archivo,
      conforme al artículo 17 de la Ley 1755 de 2015.</p>
      ${reason ? `<p style="background:#fdecea;border-left:3px solid #b3261e;padding:10px 14px;margin:16px 0;">${esc(reason)}</p>` : ""}
      <p>Este correo es la notificación del acto. Contra esta decisión procede el <strong>recurso de
      reposición</strong>${appealLastDay ? `, que puede interponer hasta el <strong>${esc(fmtDate(appealLastDay))}</strong>
      (diez días hábiles)` : " dentro de los diez días hábiles siguientes"}. Para interponerlo debe indicar sus motivos
      y aportar la información que se le había solicitado.</p>
      <p>${appealLink ? btn(appealLink, "Interponer recurso de reposición", "#b45a00") : ""}${btn(magicLink, "Ver mi radicado")}</p>
      <p style="font-size:13px;color:#666;">Si no interpone el recurso dentro del plazo, el archivo quedará en firme. En
      cualquier caso, puede radicar una nueva petición con la información completa cuando quiera.</p>
    `),
  };
}

function citizenAppealReminder({ radicado, appealLastDay, appealLink }) {
  return {
    subject: `Últimos días para el recurso de reposición — PQRSD ${radicado}`,
    html: shell("Se acaba el plazo para interponer el recurso", `
      <p>Le recordamos que la entidad decretó el desistimiento tácito de su PQRSD <strong>${esc(radicado)}</strong>.
      Si no está de acuerdo, puede interponer el <strong>recurso de reposición</strong> hasta el
      <strong>${esc(fmtDate(appealLastDay))}</strong>.</p>
      <p>Para hacerlo, indique sus motivos y aporte la información que se le había solicitado.</p>
      <p>${btn(appealLink, "Interponer recurso de reposición", "#b45a00")}</p>
      <p style="font-size:13px;color:#666;">Vencido ese día, el archivo quedará en firme. Siempre podrá radicar una
      nueva petición con la información completa.</p>
    `),
  };
}

function citizenAppealReceived({ radicado, decisionDueAt, magicLink }) {
  return {
    subject: `Recibimos su recurso de reposición — PQRSD ${radicado}`,
    html: shell("Recibimos su recurso de reposición", `
      <p>Registramos el recurso de reposición que interpuso contra el desistimiento de su PQRSD
      <strong>${esc(radicado)}</strong>, junto con la información que aportó.</p>
      <p>La entidad lo resolverá mediante una decisión motivada${decisionDueAt ? `, a más tardar el
      <strong>${esc(fmtDate(decisionDueAt))}</strong>` : ""}, y se la notificaremos por este medio.</p>
      <p>${btn(magicLink, "Ver el estado de mi PQRSD")}</p>
    `),
  };
}

function staffAppealFiled({ radicado, decisionDueAt, adminLink }) {
  return {
    subject: `Recurso de reposición interpuesto — ${radicado}`,
    html: shell("El ciudadano interpuso un recurso de reposición", `
      <p>El ciudadano interpuso recurso de reposición contra el acto que decretó el desistimiento de la PQRSD
      <strong>${esc(radicado)}</strong> y aportó la información requerida.</p>
      <p>Debe resolverse mediante decisión motivada${decisionDueAt ? ` a más tardar el
      <strong>${esc(fmtDate(decisionDueAt))}</strong> (dos meses, artículo 86 de la Ley 1437 de 2011)` : ""}:
      reponer y continuar el trámite, o confirmar el archivo.</p>
      ${staffCta(adminLink, "Resolver el recurso")}
    `),
  };
}

function citizenAppealDecided({ radicado, granted, reason, magicLink, newDeadline }) {
  return {
    subject: granted
      ? `Se repuso el desistimiento — PQRSD ${radicado}`
      : `Se confirmó el desistimiento — PQRSD ${radicado}`,
    html: shell(granted ? "Retomamos el trámite de su solicitud" : "Se confirmó el archivo de su solicitud", `
      <p>La entidad resolvió el recurso de reposición que interpuso contra el desistimiento de su PQRSD
      <strong>${esc(radicado)}</strong>.</p>
      ${granted
        ? `<p>Se <strong>repuso</strong> la decisión: la solicitud vuelve a trámite con la información que aportó.
          ${newDeadline ? `Le responderemos a más tardar el <strong>${esc(fmtDate(newDeadline))}</strong>.` : ""}</p>`
        : `<p>Se <strong>confirmó</strong> el desistimiento y el archivo. Contra esta decisión no procede recurso
          alguno; puede radicar una nueva petición con la información completa cuando quiera.</p>`}
      ${reason ? `<p style="background:#f4f5f7;border-left:3px solid #999;padding:10px 14px;margin:16px 0;">${esc(reason)}</p>` : ""}
      <p>${btn(magicLink, "Ver mi radicado")}</p>
    `),
  };
}

function citizenDesistimientoFinal({ radicado, appealLastDay, magicLink }) {
  return {
    subject: `El archivo de su PQRSD quedó en firme — ${radicado}`,
    html: shell("El archivo de su solicitud quedó en firme", `
      <p>El plazo para interponer el recurso de reposición contra el desistimiento de su PQRSD
      <strong>${esc(radicado)}</strong> venció${appealLastDay ? ` el <strong>${esc(fmtDate(appealLastDay))}</strong>` : ""}
      sin que se presentara. Por eso el archivo de la solicitud quedó en firme.</p>
      <p>Si todavía necesita lo que solicitó, puede radicar una nueva petición con la información completa en
      cualquier momento.</p>
      <p>${btn(magicLink, "Ver mi radicado")}</p>
    `),
  };
}

function staffInfoReplied({ radicado, newDeadline, adminLink }) {
  return {
    subject: `El ciudadano completó el requerimiento — ${radicado}`,
    html: shell("Información recibida del ciudadano", `
      <p>El ciudadano respondió el requerimiento de información de la PQRSD <strong>${esc(radicado)}</strong>.
      El término de respuesta se reactivó.</p>
      ${newDeadline ? `<p>Nueva fecha límite: <strong>${esc(fmtDate(newDeadline))}</strong>.</p>` : ""}
      ${staffCta(adminLink, "Revisar la respuesta del ciudadano")}
    `),
  };
}

// A drafter asked for a second pair of eyes before the requerimiento leaves the
// entity. Nothing has been required of the citizen yet and the term is still
// running, so the mail says so plainly.
function staffInfoRequestReview({ radicado, items, note, drafterName, adminLink }) {
  const list = (Array.isArray(items) ? items : [])
    .map((it) => `<li>${esc(it.kind === "file" ? `Documento: ${it.label || ""}` : it.header || "")}${it.required === false ? " (opcional)" : ""}</li>`)
    .join("");
  return {
    subject: `[PQRSD] Requerimiento por revisar — ${radicado}`,
    html: shell("Un requerimiento espera tu revisión", `
      <p>${esc(drafterName || "Un compañero")} redactó un requerimiento de información para la PQRSD
      <strong>${esc(radicado)}</strong> y pide tu revisión antes de enviarlo al ciudadano.</p>
      ${list ? `<p>Puntos a requerir:</p><ul>${list}</ul>` : ""}
      ${note ? `<p>Nota: ${esc(note)}</p>` : ""}
      <p>Puedes agregar o quitar puntos, devolverlo a borrador o aprobarlo. <strong>El término de
      respuesta de la entidad sigue corriendo</strong> hasta que se envíe.</p>
      ${staffCta(adminLink, "Revisar el requerimiento")}
    `),
  };
}

function staffInfoExpired({ radicado, dueAt, adminLink }) {
  return {
    subject: `Venció el plazo del ciudadano — ${radicado}`,
    html: shell("Requerimiento de información vencido", `
      <p>El ciudadano no completó la información requerida para la PQRSD <strong>${esc(radicado)}</strong>.
      El plazo venció el <strong>${esc(fmtDate(dueAt))}</strong>.</p>
      <p>Procede decretar el <strong>desistimiento tácito</strong> mediante acto administrativo motivado, o
      retirar el requerimiento y continuar el trámite. El sistema no archiva la solicitud automáticamente.</p>
      ${staffCta(adminLink, "Abrir la PQRSD")}
    `),
  };
}

function citizenSlaWarning({ radicado, slaDeadline, magicLink }) {
  return {
    subject: `Trabajando en su PQRSD ${radicado}`,
    html: shell("Estamos finalizando su respuesta", `
      <p>Le informamos que estamos próximos al vencimiento del plazo de respuesta para su PQRSD <strong>${esc(radicado)}</strong>.</p>
      <p>Fecha límite: <strong>${esc(fmtDate(slaDeadline))}</strong>.</p>
      <p>${btn(magicLink, "Ver estado")}</p>
    `),
  };
}

function citizenSlaBreached({ radicado, magicLink }) {
  return {
    subject: `Disculpas — su PQRSD ${radicado}`,
    html: shell("Pedimos disculpas por la demora", `
      <p>Lamentamos que su PQRSD <strong>${esc(radicado)}</strong> haya superado el plazo legal de respuesta. Nos comprometemos a entregarle una respuesta a la mayor brevedad.</p>
      <p>${btn(magicLink, "Ver estado")}</p>
    `),
  };
}

// ---------- staff templates ----------
// For anonymous PQRSDs, callers MUST pass anonymous:true so we do not leak
// solicitor data into the email. We always frame these as task updates.

// Staff emails always deep-link to the PQRSD in the admin panel when the caller
// provides `adminLink` (…/pqrsd/admin/<radicado>); the button falls back to a
// plain instruction if no link was resolved.
function staffCta(adminLink, label = "Abrir la PQRSD") {
  return adminLink
    ? `<p>${btn(adminLink, label)}</p>`
    : `<p>Ingresa al panel administrativo para gestionarla.</p>`;
}

function staffAssigned({ radicado, subjectLine, type, slaDeadline, anonymous, adminLink }) {
  return {
    subject: `[PQRSD] ${anonymous ? "Tarea PQRSD asignada" : "PQRSD asignada"} — ${radicado}`,
    html: shell("Tienes una PQRSD asignada", `
      <p>Se te ha asignado la PQRSD <strong>${esc(radicado)}</strong>.</p>
      ${anonymous ? "" : `<p>Asunto: ${esc(subjectLine || "")}</p>`}
      <p>Tipo: ${esc(PQRSD_TYPES_LABEL[type] || type || "")}</p>
      <p>Plazo: ${esc(fmtDate(slaDeadline))}</p>
      ${staffCta(adminLink, "Abrir y gestionar la PQRSD")}
    `),
  };
}

function staffForwarded({ radicado, fromName, adminLink }) {
  return {
    subject: `[PQRSD] PQRSD reenviada a ti — ${radicado}`,
    html: shell("PQRSD reenviada", `
      <p>${esc(fromName || "Un compañero")} te ha reenviado la PQRSD <strong>${esc(radicado)}</strong>.</p>
      ${staffCta(adminLink, "Abrir y continuar la gestión")}
    `),
  };
}

function staffReturned({ radicado, fromName, reason, adminLink }) {
  return {
    subject: `[PQRSD] PQRSD devuelta — ${radicado}`,
    html: shell("PQRSD devuelta para reasignación", `
      <p>${esc(fromName || "El operario asignado")} ha devuelto la PQRSD <strong>${esc(radicado)}</strong> e indica que no puede atenderla.</p>
      <p><strong>Motivo:</strong> ${esc(reason || "")}</p>
      ${staffCta(adminLink, "Abrir para reasignar")}
    `),
  };
}

function staffReassigned({ radicado, subjectLine, type, slaDeadline, anonymous, adminLink }) {
  return {
    subject: `[PQRSD] PQRSD reasignada a ti — ${radicado}`,
    html: shell("PQRSD reasignada", `
      <p>Se te ha reasignado la PQRSD <strong>${esc(radicado)}</strong>.</p>
      ${anonymous ? "" : `<p>Asunto: ${esc(subjectLine || "")}</p>`}
      <p>Tipo: ${esc(PQRSD_TYPES_LABEL[type] || type || "")}</p>
      <p>Plazo: ${esc(fmtDate(slaDeadline))}</p>
      ${staffCta(adminLink, "Abrir y gestionar la PQRSD")}
    `),
  };
}

// Sent to every member of the dependency the AI routed a fresh PQRSD to, so the
// whole group knows a new request is waiting and can decide who answers.
function staffGroupRouted({ radicado, groupName, subjectLine, type, slaDeadline, adminLink }) {
  return {
    subject: `[PQRSD] Nueva PQRSD para ${groupName || "tu dependencia"} — ${radicado}`,
    html: shell("Nueva PQRSD para tu dependencia", `
      <p>La IA clasificó y enrutó una nueva PQRSD a <strong>${esc(groupName || "tu dependencia")}</strong>. Está lista para ser atendida.</p>
      <p>Radicado: <strong>${esc(radicado)}</strong></p>
      ${subjectLine ? `<p>Asunto: ${esc(subjectLine)}</p>` : ""}
      <p>Tipo: ${esc(PQRSD_TYPES_LABEL[type] || type || "")}</p>
      <p>Plazo de respuesta: ${esc(fmtDate(slaDeadline))}</p>
      ${staffCta(adminLink, "Abrir y responder la PQRSD")}
      <p style="font-size:12px;color:#666;">Coordinen en la dependencia quién la atiende para evitar respuestas duplicadas.</p>
    `),
  };
}

function staffBreach({ radicado, kind, adminLink }) {
  const isWarning = kind === "warning";
  return {
    subject: `[PQRSD] ${isWarning ? "Plazo por vencer" : "Plazo vencido"} — ${radicado}`,
    html: shell(isWarning ? "Plazo de PQRSD por vencer" : "Plazo de PQRSD vencido", `
      <p>La PQRSD <strong>${esc(radicado)}</strong> ${isWarning ? "está próxima al vencimiento del plazo legal" : "ha superado el plazo legal de respuesta"}.</p>
      ${staffCta(adminLink, "Abrir la PQRSD")}
    `),
  };
}

// The institution's name for the module (config/pqrsd.moduleLabel), applied
// to every template's subject and HTML on the way out, so no template has to
// spell it. Set by each function next to the brand (ensureBrand).
const { relabel, relabelHtml, normalizeModuleLabel } = require("./moduleLabel");

let _moduleLabel = "";
function setModuleLabel(label) {
  _moduleLabel = normalizeModuleLabel(label);
}

const withModuleLabel = (fn) => (...args) => {
  const out = fn(...args);
  if (!out || typeof out !== "object") return out;
  // Object.assign, not object spread: the admin preview bundles this file with
  // webpack, and Babel's spread helper is an ESM import that would turn this
  // CommonJS module into an ES module with no exports.
  const relabeled = Object.assign({}, out);
  if (typeof out.subject === "string") relabeled.subject = relabel(out.subject, _moduleLabel);
  if (typeof out.html === "string") relabeled.html = relabelHtml(out.html, _moduleLabel);
  return relabeled;
};

module.exports = {
  PQRSD_TYPES_LABEL,
  setBrand,
  setModuleLabel,
  citizenSubmission: withModuleLabel(citizenSubmission),
  citizenInProgress: withModuleLabel(citizenInProgress),
  citizenResponseInterim: withModuleLabel(citizenResponseInterim),
  citizenResponseFinal: withModuleLabel(citizenResponseFinal),
  citizenSlaWarning: withModuleLabel(citizenSlaWarning),
  citizenSlaBreached: withModuleLabel(citizenSlaBreached),
  citizenInfoRequested: withModuleLabel(citizenInfoRequested),
  citizenInfoReminder: withModuleLabel(citizenInfoReminder),
  citizenInfoReceived: withModuleLabel(citizenInfoReceived),
  citizenInfoExtensionGranted: withModuleLabel(citizenInfoExtensionGranted),
  citizenDesistimiento: withModuleLabel(citizenDesistimiento),
  citizenAppealReminder: withModuleLabel(citizenAppealReminder),
  citizenAppealReceived: withModuleLabel(citizenAppealReceived),
  staffAppealFiled: withModuleLabel(staffAppealFiled),
  citizenAppealDecided: withModuleLabel(citizenAppealDecided),
  citizenDesistimientoFinal: withModuleLabel(citizenDesistimientoFinal),
  staffInfoReplied: withModuleLabel(staffInfoReplied),
  staffInfoRequestReview: withModuleLabel(staffInfoRequestReview),
  staffInfoExpired: withModuleLabel(staffInfoExpired),
  staffAssigned: withModuleLabel(staffAssigned),
  staffForwarded: withModuleLabel(staffForwarded),
  staffReturned: withModuleLabel(staffReturned),
  staffReassigned: withModuleLabel(staffReassigned),
  staffGroupRouted: withModuleLabel(staffGroupRouted),
  staffBreach: withModuleLabel(staffBreach),
};
